/**
 * Unit monthly budget (f10.10) -- the legacy `DashBoard_<unit>` row 6, as data.
 *
 * One row per Business Unit with its twelve months, the server's annual total
 * and the UNALLOCATED figure: unit budget minus its clients' budget, per
 * currency, derived on read. It replaces the legacy `New client` and
 * `Gap to budget` rows, so no pseudo-client is ever created.
 *
 * A row is saved whole, in one currency (`422 currency_mixed_in_year`). A blank
 * month is left untouched; saving 0 clears one.
 */

import { useState } from 'react';
import { Check, Pencil, RotateCw, X } from 'lucide-react';

import type { UnitMonthlyBudgetItem, UnitMonthlyBudgetSave } from '@/api/client';
import { useSaveUnitMonthlyBudget, useUnitMonthlyBudgets } from '@/api/queries';
import { EmptyCell } from '@/ui/EmptyCell';

import { useActiveCurrencies } from './ClientAnnualPanel';
import {
  MONTH_LABELS,
  conflictCurrent,
  groupDigits,
  looksLikeAmount,
  normaliseAmount,
  refusalMessage,
  unallocatedPill,
} from './budgetView';

const MONTH_KEYS = MONTH_LABELS.map((_, index) => String(index + 1));

export function UnitMonthlyPanel({ year }: { year: number }) {
  const budgets = useUnitMonthlyBudgets(year);
  const currencies = useActiveCurrencies();
  const [editing, setEditing] = useState<string | null>(null);

  if (budgets.isPending) return <p className="hint">Loading the {year} unit budgets…</p>;
  if (budgets.isError) {
    return (
      <div className="alert alert-danger" role="alert">
        <div className="a-body">{refusalMessage(budgets.error)}</div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => void budgets.refetch()}>
          <RotateCw aria-hidden="true" /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>
          Monthly budget per Business Unit <span className="hint">· {year}</span>
        </h3>
        <span className="hint">Unallocated = unit budget − its clients&rsquo; annual budget, per currency</span>
      </div>
      {budgets.data.items.length === 0 ? (
        <div className="card-body">
          <div className="empty">
            <h3>No Business Units yet</h3>
            <p>Business Units arrive through the master import.</p>
          </div>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="data compact budget-table budget-months">
            <thead>
              <tr>
                <th className="budget-sticky">Business Unit</th>
                <th>Cur.</th>
                {MONTH_LABELS.map((label) => (
                  <th key={label} className="num">
                    {label}
                  </th>
                ))}
                <th className="num budget-total-col">Total</th>
                <th>Unallocated</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {budgets.data.items.map((item) =>
                editing === item.business_unit.id ? (
                  <EditRow key={item.business_unit.id} item={item} year={year} currencies={currencies}
                    onDone={() => setEditing(null)} />
                ) : (
                  <ViewRow key={item.business_unit.id} item={item} onEdit={() => setEditing(item.business_unit.id)} />
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Unallocated({ item }: { item: UnitMonthlyBudgetItem }) {
  if (item.unallocated.length === 0) return <EmptyCell />;
  return (
    <div className="budget-chips">
      {item.unallocated.map((money) => (
        <span key={money.currency_code} className={unallocatedPill(money.amount)}
          title={`Clients: ${item.client_annual_total.find((m) => m.currency_code === money.currency_code)?.amount ?? '0.00'} ${money.currency_code}`}>
          {groupDigits(money.amount)} {money.currency_code}
        </span>
      ))}
    </div>
  );
}

function ViewRow({ item, onEdit }: { item: UnitMonthlyBudgetItem; onEdit: () => void }) {
  return (
    <tr className={item.business_unit.is_active ? undefined : 'is-muted'}>
      <td className="budget-sticky">
        {item.business_unit.name}
        {!item.business_unit.is_active && <span className="badge pill-deactivated budget-inline-badge">Inactive</span>}
      </td>
      <td className="mono">{item.currency_code}</td>
      {MONTH_KEYS.map((key) => {
        const month = item.months[key];
        return (
          <td key={key} className="num budget-amount">
            {month ? groupDigits(month.amount) : <EmptyCell />}
          </td>
        );
      })}
      <td className="num budget-amount budget-total-col">
        {item.annual_total ? groupDigits(item.annual_total) : <EmptyCell />}
      </td>
      <td>
        <Unallocated item={item} />
      </td>
      <td className="budget-actions">
        {item.business_unit.is_active && (
          <button type="button" className="btn btn-secondary btn-sm btn-icon" onClick={onEdit}
            aria-label={`Edit ${item.business_unit.name}`} title="Edit the twelve months">
            <Pencil aria-hidden="true" />
          </button>
        )}
      </td>
    </tr>
  );
}

function EditRow({
  item,
  year,
  currencies,
  onDone,
}: {
  item: UnitMonthlyBudgetItem;
  year: number;
  currencies: readonly string[];
  onDone: () => void;
}) {
  const save = useSaveUnitMonthlyBudget(year);
  const [currency, setCurrency] = useState(item.currency_code);
  const [cells, setCells] = useState<Record<string, string>>(() =>
    Object.fromEntries(MONTH_KEYS.map((key) => [key, item.months[key]?.amount ?? ''])),
  );
  const [versions, setVersions] = useState<Record<string, number | null>>(() =>
    Object.fromEntries(MONTH_KEYS.map((key) => [key, item.months[key]?.version ?? null])),
  );
  const cell = (key: string) => cells[key] ?? '';
  const invalid = MONTH_KEYS.filter((key) => cell(key) !== '' && !looksLikeAmount(cell(key)));
  const conflict = conflictCurrent(save.error);
  const options = currencies.includes(currency) ? currencies : [currency, ...currencies];

  const submit = () => {
    if (invalid.length) return;
    const months: UnitMonthlyBudgetSave['months'] = {};
    for (const key of MONTH_KEYS) {
      // A blank month is sent as null: left untouched, never cleared.
      months[key] = cell(key) === '' ? null : { amount: normaliseAmount(cell(key)), version: versions[key] ?? null };
    }
    save.mutate({ unitId: item.business_unit.id, body: { currency_code: currency, months } }, { onSuccess: onDone });
  };

  return (
    <>
      <tr className="budget-edit-row">
        <td className="budget-sticky">{item.business_unit.name}</td>
        <td>
          <select aria-label="Currency" value={currency} onChange={(event) => setCurrency(event.target.value)}>
            {options.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </td>
        {MONTH_KEYS.map((key, index) => (
          <td key={key} className="num">
            <input
              type="text"
              inputMode="decimal"
              className={`budget-input budget-input-month${invalid.includes(key) ? ' has-problem' : ''}`}
              aria-label={`${item.business_unit.name} ${MONTH_LABELS[index]} ${year}`}
              value={cell(key)}
              autoFocus={index === 0}
              onChange={(event) => setCells((current) => ({ ...current, [key]: event.target.value }))}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submit();
                if (event.key === 'Escape') onDone();
              }}
            />
          </td>
        ))}
        <td className="num budget-total-col hint">server total</td>
        <td className="hint">{currency !== item.business_unit.reporting_currency_code
          ? `Reports in ${item.business_unit.reporting_currency_code}` : ''}</td>
        <td className="budget-actions">
          <div className="btn-row">
            <button type="button" className="btn btn-primary btn-sm btn-icon" onClick={submit}
              disabled={invalid.length > 0 || save.isPending} aria-label="Save">
              <Check aria-hidden="true" />
            </button>
            <button type="button" className="btn btn-secondary btn-sm btn-icon" onClick={onDone} aria-label="Cancel">
              <X aria-hidden="true" />
            </button>
          </div>
        </td>
      </tr>
      {(save.error || invalid.length > 0) && (
        <tr className="budget-feedback-row">
          <td colSpan={MONTH_KEYS.length + 5}>
            {conflict ? (
              <div className="alert alert-warning" role="alert">
                <div className="a-body">
                  {conflict.month ? `${MONTH_LABELS[conflict.month - 1]} ` : ''}changed since you opened it: it is now{' '}
                  <strong className="mono">
                    {groupDigits(conflict.amount)} {conflict.currency_code}
                  </strong>
                  {conflict.updated_by ? ` (${conflict.updated_by})` : ''}. Re-apply yours to overwrite it.
                </div>
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => {
                    if (conflict.month) {
                      const key = String(conflict.month);
                      setVersions((current) => ({ ...current, [key]: conflict.version }));
                    }
                    save.reset();
                  }}>
                  Re-apply mine
                </button>
              </div>
            ) : (
              <div className="field-error" role="alert">
                {invalid.length > 0
                  ? `Amounts carry at most two decimals: check ${invalid.map((k) => MONTH_LABELS[Number(k) - 1]).join(', ')}.`
                  : refusalMessage(save.error)}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
